import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { uiState } from 'react-redux-ui-state';
import { withStyles } from '@material-ui/styles';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Done from '@material-ui/icons/Done';
import NotificationImportant from '@material-ui/icons/NotificationImportant';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import TableHead from '@material-ui/core/TableHead';

import ORTable from 'components/ORTable/ORTable';
import ORTypography from 'components/ORTypography/ORTypography';
import ConnectWarning from 'components/MissionCtrl/InstancesPage/InstanceDetail/ConnectTab/ConnectWarning/ConnectWarning';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  instanceId: PropTypes.string.isRequired,
  instanceService: PropTypes.string.isRequired,
  backups: PropTypes.instanceOf(Array),
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setUiState: PropTypes.func.isRequired,
  pitrBackupInfo: PropTypes.shape({
    message: PropTypes.string,
    lastSnapshotTimeStamp: PropTypes.string,
    lastPitrChunkTimeStamp: PropTypes.string,
    state: PropTypes.string,
    status: PropTypes.number,
  }),
};

const defaultProps = {
  backups: [],
  pitrBackupInfo: {},
};

const styles = (theme) => ({
  emptyBackupsMsg: {
    textAlign: 'center',
  },
  tableCellRoot: {
    width: '75%',
    padding: '7px 8px 7px 8px',
    fontSize: '12px !important',
  },
  tableCellRootIcon: {
    width: '25%',
    padding: '7px 8px 7px 8px',
  },
  tableHeader: {
    '& th': {
      height: '20px',
      padding: '15px 8px 5px 8px',
    },
  },
  notificationText: {
    color: theme.customColors.red,
    fontStyle: 'italic',
    width: '100%',
    fontSize: '10px !important',
  },
  successText: {
    color: theme.palette.secondary.main,
  },
  warningText: {
    color: theme.palette.warning.main,
  },
  criticalText: {
    color: theme.palette.error.main,
  },
  unknownText: {
    color: theme.customColors.gray,
  },
  checkmarkRoot: {
    color: theme.palette.secondary.main,
    float: 'right',
    fontSize: '2rem',
  },
  notificationRoot: {
    color: theme.customColors.red,
    float: 'right',
    fontSize: '2rem',
  },
  estimateRoot: {
    color: theme.customColors.burntYellow,
    float: 'right',
    fontSize: '2rem',
  },
  pitrBackupInfo: {
    margin: "16px 0",
    padding: "8px",
    boxShadow: "4px 5px 5px 0px rgb(216 216 216)",
    borderRadius: "4px",
    backgroundColor: theme.customColors.lightGrey,
  }
});

export function UnstyledBackupList({
  classes,
  backups,
  page,
  rowsPerPage,
  instanceService,
  setUiState,
  pitrBackupInfo,
}) {
  function handleChangePage(event, newPage) {
    setUiState({
      page: newPage,
    });
  }

  const noBackups = (
    <ORTypography component="p" className={classes.emptyBackupsMsg}>
      You have no backups. If you need a backup restored, please contact&nbsp;
      <a href="https://objectrocket.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer">
        support
      </a>
      .
    </ORTypography>
  );

  const notificationText = (
    <span className={classes.notificationText}>
      This backup is experiencing delays. Support is currently working on fixing the error.&nbsp;
      <a href="https://objectrocket.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer">
        View the Zendesk Ticket
      </a>
      .
    </span>
  );

  const successText = <span className={classes.successText}>Success</span>;

  const boldText = (text) => (
    <strong>
      {text}
      :&nbsp;
    </strong>
  );

  const backupItems = backups
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((backup) => {
      const start = moment.utc(backup.start).format('YYYY/MM/DD h:mm:ss A z');
      const end = backup.end === null ? '(TBD)' : moment.utc(backup.end).format('YYYY/MM/DD h:mm:ss A z');
      const { id } = backup;

      return (
        <TableRow key={id} style={{ verticalAlign: 'top' }}>
          <TableCell classes={{ root: classes.tableCellRoot }} align="left">
            {boldText('Backup ID')}
            {id}
            <br />
            {boldText('Start')}
            {start}
            <br />
            {boldText('End')}
            {end}
            <br />
            {boldText('Status')}
            {backup.status !== 2 ? notificationText : successText}
            <br />
          </TableCell>
          <TableCell classes={{ root: classes.tableCellRootIcon }} align="center">
            {backup.status === 1 && (
              <ErrorOutline classes={{ root: classes.estimateRoot }} fontSize="large" />
            )}
            {backup.status === 2 && (
              <Done classes={{ root: classes.checkmarkRoot }} fontSize="large" />
            )}
            {backup.status === 3 && (
              <NotificationImportant
                classes={{ root: classes.notificationRoot }}
                fontSize="large"
              />
            )}
          </TableCell>
        </TableRow>
      );
    });

  const mongoDbPitrBanner = (
    <ConnectWarning>
      <ORTypography variant="altSubheading">
        MongoDB Point-In-Time-Recovery backups
      </ORTypography>
      <ORTypography variant="caption">
        <p>
          In addition to full backups, ObjectRocket also provides Point-In-Time-Recovery (PITR)
          backups, which are enabled by default. With PITR enabled, the operations log (an internal
          MongoDB log of all operations performed on the shard, also called oplog slices or chunks)
          will be backed up every 10 minutes, concurrently, from each shard.
        </p>
      </ORTypography>
      <ORTypography variant="caption">
        <p>
          Visit the {' '}
          <a
            href="https://docs.objectrocket.com/mongodb_backup_and_recovery_cloud.html?#backups"
            target="_blank"
            rel="noopener noreferrer"
          >
            documentation
          </a> to learn more about MongoDB Backups.
        </p>
      </ORTypography>
    </ConnectWarning>
  );

  const pitrNotificationText = (
    <span className={classes.notificationText}>Backup information currently unavailable</span>
  );

  const status = (pitrStatus) => {
    switch (pitrStatus) {
    case 0:
      return successText;
    case 1:
      return <span className={classes.warningText}>Warning</span>;
    case 2:
      return <span className={classes.criticalText}>Critical</span>;
    case null:
      return pitrNotificationText;
    default:
      return <span className={classes.unknownText}>Unknown</span>;
  }
};

  const lastSnapshotTimeStamp = pitrBackupInfo.lastSnapshotTimeStamp === undefined ? '' : moment.utc(pitrBackupInfo.lastSnapshotTimeStamp).format('YYYY/MM/DD h:mm:ss A z');
  const lastPitrChunkTimeStamp = pitrBackupInfo.lastPitrChunkTimeStamp === undefined ? '' : moment.utc(pitrBackupInfo.lastPitrChunkTimeStamp).format('YYYY/MM/DD h:mm:ss A z');

  const mongoDbPitrBackupInfo = (
    <div className={classes.pitrBackupInfo} id="pitrBackupInfo">
      <ORTypography variant="altSubheading">Point-In-Time-Recovery Backup Details</ORTypography>
      <ORTypography variant="caption">
        <div>
          {boldText('Last Full backup timestamp')}
          {lastSnapshotTimeStamp}
          <br />
          {boldText('Last PITR chunk timestamp')}
          {lastPitrChunkTimeStamp}
          <br />
          {boldText('Status')}
          {status(pitrBackupInfo.status)}
        </div>
      </ORTypography>
    </div>
  );

  const backupTabPitrInfo = (
    <div>
      {instanceService === "MongoDB" ? mongoDbPitrBanner : null}
      {instanceService === "MongoDB" ? mongoDbPitrBackupInfo : null}
    </div>
  )

  const backupTable =
    backups.length === 0 ? (
      <div>{noBackups}{backupTabPitrInfo}</div>
    ) : (
      <div>
        {backupTabPitrInfo}
        <ORTable
          count={backups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
        >
          <TableHead classes={{ root: classes.tableHeader }}>
            <TableRow>
              <TableCell>Backup Details</TableCell>
            </TableRow>
          </TableHead>
            <TableBody>{backupItems}</TableBody>
        </ORTable>
      </div>
    );

  return backupTable;
}

UnstyledBackupList.propTypes = propTypes;
UnstyledBackupList.defaultProps = defaultProps;

export const uiConfig = {
  name: 'backupsTable',
  state: () => ({
    page: 0,
    rowsPerPage: 10,
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledBackupList));
